/**
 * CoachPanel — The Coach docked on the right of the editor. Sessions started
 * here attach to the current script (the coach can read the draft for
 * context). Past conversations are browsable from the history dropdown.
 */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CoachChat } from "@/components/coach/CoachChat";
import { X, Compass, ChevronDown, Plus, FileText } from "lucide-react";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
}

export function CoachPanel({ scriptId, open, onClose, embedded = false, onBeforeSend, prefill }) {
  const [sessions, setSessions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if ((!open && !embedded) || loaded) return;
    api.get("/coach/sessions").then(({ data }) => {
      const list = data.sessions || [];
      setSessions(list);
      const forScript = list.find((s) => s.script_id === scriptId);
      if (forScript) setSelectedId(forScript.id);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [open, loaded, scriptId]);

  const ensureSession = useCallback(async () => {
    const { data } = await api.post("/coach/sessions", { script_id: scriptId });
    setSessions((prev) => [data.session, ...prev]);
    return data.session.id;
  }, [scriptId]);

  const onTitle = useCallback((title, sid) => {
    setSessions((prev) => prev.map((s) => (s.id === sid ? { ...s, title } : s)));
    setSelectedId((cur) => cur || sid);
  }, []);

  if (!open && !embedded) return null;

  const selected = sessions.find((s) => s.id === selectedId) || null;

  const inner = (
    <>
      {/* session picker */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-[var(--line)] shrink-0 relative">
        <button onClick={() => setMenuOpen((o) => !o)} className="flex-1 min-w-0 text-left text-xs text-[var(--ink-secondary)] inline-flex items-center gap-1.5 hover:text-[var(--ink-primary)]" data-testid="coach-session-picker">
          <ChevronDown size={13} className="shrink-0" />
          <span className="truncate">{selected ? selected.title : "New conversation"}</span>
        </button>
        <button onClick={() => { setSelectedId(null); setMenuOpen(false); }} className="text-xs text-[var(--brand)] inline-flex items-center gap-1 hover:opacity-80 shrink-0" data-testid="coach-new-session">
          <Plus size={13} /> New
        </button>
        {menuOpen && (
          <div className="absolute left-4 right-4 top-9 max-h-72 overflow-auto bg-[var(--paper)] border border-[var(--line)] rounded-lg shadow-xl py-1 z-30" data-testid="coach-session-menu">
            {sessions.length === 0 && <p className="text-sm text-[var(--ink-muted)] px-3 py-2">No conversations yet.</p>}
            {sessions.map((s) => (
              <button key={s.id} onClick={() => { setSelectedId(s.id); setMenuOpen(false); }} className={`w-full text-left px-3 py-2 hover:bg-[var(--warm-accent)] ${s.id === selectedId ? "bg-[var(--brand-soft)]" : ""}`} data-testid={`coach-session-${s.id}`}>
                <div className="text-sm text-[var(--ink-primary)] truncate">{s.title}</div>
                <div className="flex items-center gap-2 text-[10px] text-[var(--ink-muted)] mt-0.5">
                  {fmtDate(s.updated_at)}
                  {s.script_title && <span className="inline-flex items-center gap-1 truncate"><FileText size={9} /> {s.script_title}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <CoachChat key={selectedId || "new"} sessionId={selectedId} ensureSession={ensureSession} onTitle={onTitle} onBeforeSend={onBeforeSend} prefill={prefill} />
    </>
  );

  if (embedded) {
    return <div className="flex flex-col h-full min-h-0" data-testid="coach-panel">{inner}</div>;
  }

  return (
    <div className="fixed inset-0 z-40 w-full sm:relative sm:inset-auto sm:w-[420px] sm:shrink-0 sm:h-full bg-[var(--paper)] border-l border-[var(--line)] shadow-[-4px_0_24px_rgba(0,0,0,0.10)] sm:shadow-none flex flex-col" data-testid="coach-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--line)] shrink-0">
        <div className="min-w-0">
          <span className="flex items-center gap-2 font-display text-lg text-[var(--ink-primary)]"><Compass size={17} className="text-[var(--brand)]" /> The Coach</span>
          <p className="text-[11px] text-[var(--ink-muted)] mt-0.5">Draws the story out of you — never writes it for you.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-[var(--warm-accent)] text-[var(--ink-secondary)]" data-testid="coach-close"><X size={17} /></button>
      </div>
      {inner}
    </div>
  );
}
